import {
  queryOptions,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import { useCallback, useEffect, useReducer, useRef } from 'react';

import { withCorrelationId } from '../../../../../services/usageContext.js';
import { useUserBi } from '../../../../../telemetry/hooks/useUserBi/useUserBi.js';
import {
  EntityType,
  OperationType,
  PanelType,
} from '../../../../../telemetry/userbi.js';
import type {
  AnnotationPersistenceDef,
  SavedAnnotation,
} from '../annotationTypes/annotationType.interface.js';
import type { LabeledBy } from '../expectedAction/expectedActionPayload.js';
import type { AnnotationTarget } from '../save/annotationDocument.js';
import {
  AnnotationSaveRecoveryError,
  loadExpectedActions,
  recoverAnnotationSave,
  saveExpectedActions,
} from '../save/expectedActionStore.js';
import type { AnnotationService } from '../save/expectedActionStore.js';

export interface AnnotationInstance extends SavedAnnotation {
  instanceId: string;
  expanded: boolean;
}
interface SessionState {
  instances: AnnotationInstance[];
  revision: number;
  savedRevision: number;
}
type SessionAction =
  | { type: 'hydrate'; actions: SavedAnnotation[] }
  | { type: 'replace'; instances: AnnotationInstance[]; changed: boolean }
  | { type: 'saved'; revision: number };
const INITIAL_STATE: SessionState = {
  instances: [],
  revision: 0,
  savedRevision: 0,
};
export function annotationSessionReducer(
  state: SessionState,
  action: SessionAction,
): SessionState {
  switch (action.type) {
    case 'hydrate':
      return {
        ...INITIAL_STATE,
        instances: action.actions.map((value) => ({
          ...value,
          instanceId: crypto.randomUUID(),
          expanded: false,
        })),
      };
    case 'replace':
      return {
        ...state,
        instances: action.instances,
        revision: state.revision + (action.changed ? 1 : 0),
      };
    case 'saved':
      return {
        ...state,
        savedRevision: Math.max(state.savedRevision, action.revision),
      };
  }
}

export function annotationDocumentOptions(
  service: AnnotationService,
  target: AnnotationTarget,
  annotationType: AnnotationPersistenceDef,
) {
  return queryOptions({
    queryKey: [
      'annotation-document',
      target.dataSetId,
      target.sourceDatasetVersion,
      target.sourceDataId,
      target.sourceFileId,
      target.annotationSetId,
      target.draftVersion,
      target.annotationDataId,
      target.documentId,
      target.schemaId,
      target.fileId,
    ],
    queryFn: () => loadExpectedActions({ service, target, annotationType }),
    staleTime: Infinity,
    retry: false,
  });
}

export function useAnnotationSession(
  service: AnnotationService,
  initialTarget: AnnotationTarget,
  annotationType: AnnotationPersistenceDef,
  sourceContentHash: () => Promise<string>,
  labeledBy?: LabeledBy,
  onSaved?: (hasAnnotations: boolean) => void,
) {
  const client = useQueryClient();
  const { trackOperation } = useUserBi();
  const query = useQuery(
    annotationDocumentOptions(service, initialTarget, annotationType),
  );
  const [state, dispatch] = useReducer(annotationSessionReducer, INITIAL_STATE);
  const current = useRef(state);
  current.current = state;
  const target = useRef(initialTarget);
  const recovery = useRef<AnnotationSaveRecoveryError>();
  const saving = useRef(false);
  const hydrated = useRef(false);
  useEffect(() => {
    if (query.data !== undefined && !hydrated.current) {
      dispatch({ type: 'hydrate', actions: query.data });
      hydrated.current = true;
    }
  }, [query.data]);

  const mutation = useMutation({
    mutationFn: (snapshot: { actions: SavedAnnotation[]; revision: number }) =>
      trackOperation(
        {
          panelType: PanelType.AnnotationEditorPage,
          entityType: EntityType.AnnotationData,
          operation: OperationType.Save,
        },
        async (correlationId) => {
          try {
            target.current = await saveExpectedActions({
              service,
              target: target.current,
              actions: snapshot.actions,
              annotationType,
              sourceContentHash: await sourceContentHash(),
              labeledBy,
              usage: { extras: withCorrelationId(correlationId) },
            });
          } catch (error) {
            if (error instanceof AnnotationSaveRecoveryError)
              recovery.current = error;
            throw error;
          }
          return snapshot;
        },
      ),
    onSuccess: (snapshot) => {
      dispatch({ type: 'saved', revision: snapshot.revision });
      client.setQueryData(
        annotationDocumentOptions(service, initialTarget, annotationType)
          .queryKey,
        snapshot.actions,
      );
      void client.invalidateQueries({ queryKey: ['annotation-authoring'] });
      onSaved?.(snapshot.actions.length > 0);
    },
    onSettled: () => {
      saving.current = false;
    },
  });

  const save = useCallback(
    (
      instances = current.current.instances,
      revision = current.current.revision,
    ) => {
      if (
        saving.current ||
        recovery.current !== undefined ||
        query.isPending ||
        query.isError
      )
        return;
      saving.current = true;
      mutation.mutate({
        actions: instances
          .filter((instance) => instance.draft !== null)
          .map(({ turnId, afterEventIndex, draft }) => ({
            turnId,
            afterEventIndex,
            draft,
          })),
        revision,
      });
    },
    [mutation, query.isPending, query.isError],
  );

  const replace = useCallback(
    (instances: AnnotationInstance[], changed: boolean) => {
      const next = {
        ...current.current,
        instances,
        revision: current.current.revision + (changed ? 1 : 0),
      };
      current.current = next;
      dispatch({ type: 'replace', instances, changed });
      return next.revision;
    },
    [],
  );

  // Cleanup retry confirms the original saved snapshot; it must not reupload it.
  const reconciliation = useMutation({
    mutationFn: () =>
      trackOperation(
        {
          panelType: PanelType.AnnotationEditorPage,
          entityType: EntityType.AnnotationData,
          operation: OperationType.Save,
        },
        async (correlationId) => {
          const pending = recovery.current;
          if (pending === undefined)
            throw new Error('No annotation save is awaiting reconciliation.');
          target.current = await recoverAnnotationSave(service, pending, {
            extras: withCorrelationId(correlationId),
          });
          recovery.current = undefined;
        },
      ),
    onSuccess: () => {
      if (mutation.variables !== undefined) {
        dispatch({ type: 'saved', revision: mutation.variables.revision });
        client.setQueryData(
          annotationDocumentOptions(service, initialTarget, annotationType)
            .queryKey,
          mutation.variables.actions,
        );
        onSaved?.(mutation.variables.actions.length > 0);
      }
      void client.invalidateQueries({ queryKey: ['annotation-authoring'] });
      mutation.reset();
    },
  });
  const isSaving = mutation.isPending || reconciliation.isPending;
  const dirty = state.revision !== state.savedRevision;
  const shouldBlock =
    dirty ||
    isSaving ||
    state.instances.some((instance) => instance.expanded) ||
    recovery.current !== undefined;
  useEffect(() => {
    if (!shouldBlock) return;
    const listener = (event: BeforeUnloadEvent) => {
      event.preventDefault();
      event.returnValue = '';
    };
    window.addEventListener('beforeunload', listener);
    return () => window.removeEventListener('beforeunload', listener);
  }, [shouldBlock]);
  return {
    state,
    replace,
    save,
    dirty,
    shouldBlock,
    isSaving,
    isLoading: query.isPending,
    loadError: query.error,
    error: reconciliation.error ?? mutation.error,
    needsRecovery: recovery.current !== undefined,
    reconcile: () => reconciliation.mutate(),
    refetch: () => query.refetch(),
    target: target.current,
  };
}
